import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  ChevronRight,
  Fullscreen,
  FullscreenExit,
  Lock,
  PlayArrow,
  Quiz,
  RadioButtonUnchecked,
  Schedule,
  Videocam,
} from "@mui/icons-material";

import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import CourseProgressBar from "./components/CourseProgressBar";
import Loader from "components/loader";
import { AppContext } from "context/appContext";
import { assessmentApi, certificateApi, COMPCHECK_URL, courseApi } from "configs/api";
import { Course, CourseModule, ModuleContent } from "slices/courseSlice";
import { useCourse } from "hooks/useCourse";
import useAuth from "hooks/useAuth";
import { ConfirmAlert } from "components/swalAlert";

type PlayerContent = ModuleContent & {
  progress: number;
  watchedSeconds: number;
  completed: boolean;
  completedAt?: string;
};

type PlayerAssessmentSummary = {
  id: string;
  title: string;
  passingScore: number;
  unlocksModuleID?: string;
  passed: boolean;
};

type AssessmentAttemptAnswer = {
  questionID: string;
  selectedOptions: string[];
};

type AssessmentAttempt = {
  id: string;
  score: number;
  percentage: number;
  passed: boolean;
  answers?: AssessmentAttemptAnswer[];
};

type PlayerModule = Omit<CourseModule, "content"> & {
  isLocked: boolean;
  isCompleted: boolean;
  requiresAssessment: boolean;
  assessmentPassed: boolean;
  assessments: PlayerAssessmentSummary[];
  content: PlayerContent[];
};

type PlayerStatePayload = {
  enrollment: {
    id: string;
    progress: number;
    status: string;
    currentModuleID?: string;
  };
  overallProgress: number;
  modules: PlayerModule[];
};

type CompletionCertificate = {
  id?: string;
  certificateNumber?: string;
  pdfUrl?: string;
};

type AssessmentQuestion = {
  id: string;
  question: string;
  points: number;
  options: Array<{
    id: string;
    text: string;
    isCorrect: boolean;
  }>;
};

type PlayerAssessment = {
  id: string;
  moduleID?: string;
  title: string;
  passingScore: number;
  unlocksModuleID?: string;
  questions: AssessmentQuestion[];
};

function CoursePlayer(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getCourse } = useCourse();
  const { showAlert } = React.useContext(AppContext);
  const { isCompany, isManagerOrHR, user } = useAuth();
  const showAlertRef = React.useRef(showAlert);

  const [course, setCourse] = React.useState<Course | null>(null);
  const [playerState, setPlayerState] = React.useState<PlayerStatePayload | null>(null);
  const [assessments, setAssessments] = React.useState<PlayerAssessment[]>([]);
  const [activeModuleId, setActiveModuleId] = React.useState<string | null>(null);
  const [activeContentId, setActiveContentId] = React.useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = React.useState<Record<string, string[]>>({});
  const [loading, setLoading] = React.useState(true);
  const [syncing, setSyncing] = React.useState(false);
  const [submittingAssessment, setSubmittingAssessment] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [videoLoading, setVideoLoading] = React.useState(true);
  const [inlineError, setInlineError] = React.useState<string | null>(null);
  const [isAssessmentActive, setIsAssessmentActive] = React.useState(false);
  const [activeAssessmentId, setActiveAssessmentId] = React.useState<string | null>(null);
  const [assessmentAttempts, setAssessmentAttempts] = React.useState<Record<string, AssessmentAttempt | null>>({});
  const [hasWatchedRecord, setHasWatchedRecord] = React.useState<boolean | null>(null);
  const [completionDialogOpen, setCompletionDialogOpen] = React.useState(false);
  const [completionCertificate, setCompletionCertificate] = React.useState<CompletionCertificate | null>(null);
  const [downloadingCertificate, setDownloadingCertificate] = React.useState(false);

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const playerShellRef = React.useRef<HTMLDivElement | null>(null);
  const lastSyncedSecondsRef = React.useRef(0);
  const lastPlaybackPositionRef = React.useRef(0);
  const accumulatedWatchedRef = React.useRef(0);
  const activeModuleIdRef = React.useRef<string | null>(null);
  const activeContentIdRef = React.useRef<string | null>(null);
  const watchRegistrationAttemptedRef = React.useRef(false);
  const watchSeatNoticeShownRef = React.useRef(false);
  const previousCompletedRef = React.useRef(false);

  const hasBranchScope = React.useMemo(() => {
    const branchValue = user?.branchID;
    if (branchValue === null || branchValue === undefined) return false;
    if (typeof branchValue === "string") return branchValue.trim().length > 0;
    return true;
  }, [user?.branchID]);

  /**
   * Company and central manager/HR always; branch-scoped manager/HR when access comes from org seat pools
   * (branch, direct user assignment, or company row), not from explore/creator-only paths.
   */
  const needsSeatConsumptionNotice = React.useMemo(() => {
    if (isCompany) return true;
    if (!isManagerOrHR) return false;
    if (!hasBranchScope) return true;
    const vs = course?.visibilitySource;
    return (
      vs === "assigned_to_branch" ||
      vs === "assigned_to_me" ||
      vs === "assigned_to_company"
    );
  }, [isCompany, isManagerOrHR, hasBranchScope, course?.visibilitySource]);

  React.useEffect(() => {
    showAlertRef.current = showAlert;
  }, [showAlert]);

  React.useEffect(() => {
    activeModuleIdRef.current = activeModuleId;
  }, [activeModuleId]);

  React.useEffect(() => {
    activeContentIdRef.current = activeContentId;
  }, [activeContentId]);

  const applyPlayerState = React.useCallback((nextState: PlayerStatePayload) => {
    setPlayerState(nextState);
    const unlockedModules = nextState.modules.filter((module) => !module.isLocked);
    const preferredModule = unlockedModules.find((module) => module.id === nextState.enrollment.currentModuleID)
      || unlockedModules.find((module) => module.id === activeModuleIdRef.current)
      || unlockedModules[0]
      || nextState.modules[0]
      || null;

    if (!preferredModule) {
      setActiveModuleId(null);
      setActiveContentId(null);
      return;
    }

    setActiveModuleId(preferredModule.id || null);
    const nextContent = preferredModule.content.find((content) => content.id === activeContentIdRef.current)
      || preferredModule.content.find((content) => !content.completed)
      || preferredModule.content[0]
      || null;
    setActiveContentId(nextContent?.id || null);
  }, []);

  const loadData = React.useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setInlineError(null);
    try {
      const [courseResponse, stateResponse, assessmentResponse] = await Promise.all([
        courseApi.get(`/api/courses/${id}`),
        courseApi.get(`/api/courses/${id}/player-state`),
        assessmentApi.get(`/api/assessments/course/${id}`),
      ]);

      const coursePayload = courseResponse?.data?.data || courseResponse?.data || null;
      const playerPayload = stateResponse.data?.data || stateResponse.data;
      const assessmentPayload = assessmentResponse.data?.data || assessmentResponse.data || [];

      setCourse(coursePayload);
      setAssessments(Array.isArray(assessmentPayload) ? assessmentPayload : []);
      applyPlayerState(playerPayload);
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to load course player";
      setInlineError(message);
      showAlertRef.current(message, "error", "Error");
    } finally {
      setLoading(false);
    }
  }, [applyPlayerState, id]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    if (!id || !course) return;

    if (!needsSeatConsumptionNotice) {
      setHasWatchedRecord(true);
      return;
    }

    const fetchWatchStatus = async () => {
      try {
        const response = await courseApi.get("/api/courses/watched", { skipAutoLogout: true } as any);
        const watchedEntries = response.data?.data || [];
        const alreadyWatched = Array.isArray(watchedEntries)
          ? watchedEntries.some((entry: any) => String(entry.courseID) === String(id))
          : false;
        setHasWatchedRecord(alreadyWatched);
      } catch {
        setHasWatchedRecord(false);
      }
    };

    void fetchWatchStatus();
  }, [course, id, needsSeatConsumptionNotice]);

  React.useEffect(() => {
    if (!id || !course || !playerState || watchRegistrationAttemptedRef.current || hasWatchedRecord === null) {
      return;
    }

    const registerWatch = async () => {
      try {
        const purchasedQuotaAvailable = Number(course.purchaseInfo?.quotaAvailable ?? 0);
        const freeQuotaAvailable = Number(course.freeAssignmentInfo?.quotaAvailable ?? 0);
        const hasPaidPurchasedAllowance = Boolean(course.purchaseInfo?.hasAccess && (purchasedQuotaAvailable === -1 || purchasedQuotaAvailable > 0));
        const hasFreeAssignmentAllowance = Boolean(course.assignmentIsFreeAccess && (freeQuotaAvailable === -1 || freeQuotaAvailable > 0));
        const consumesSeatOnFirstWatch =
          (hasPaidPurchasedAllowance && purchasedQuotaAvailable !== -1) ||
          (hasFreeAssignmentAllowance && freeQuotaAvailable !== -1);
        const hasUnlimitedOrgPool =
          (hasFreeAssignmentAllowance && freeQuotaAvailable === -1) ||
          (hasPaidPurchasedAllowance && purchasedQuotaAvailable === -1);
        const showSeatUsageDialog =
          consumesSeatOnFirstWatch || (needsSeatConsumptionNotice && hasUnlimitedOrgPool);

        if (needsSeatConsumptionNotice && showSeatUsageDialog && !hasWatchedRecord && !watchSeatNoticeShownRef.current) {
          watchSeatNoticeShownRef.current = true;
          const confirmed = await ConfirmAlert(
            "This is the first watch for this course and it will consume 1 seat from your allowance. Press OK to continue watching.",
            "Seat Usage Notice",
            "OK"
          );

          if (!confirmed) {
            navigate(`/courses/${id}`);
            return;
          }
        }

        watchRegistrationAttemptedRef.current = true;
        await courseApi.post(`/api/courses/watch`, { courseID: id }, { skipAutoLogout: true } as any);
      } catch (error: any) {
        const message = error.response?.data?.message || "Failed to register course watch";
        showAlertRef.current(message, "error", "Error");
      }
    };

    void registerWatch();
  }, [course, hasWatchedRecord, id, navigate, needsSeatConsumptionNotice, playerState]);

  React.useEffect(() => {
    const isCompleted =
      Boolean(playerState?.enrollment?.status === "completed") ||
      Number(playerState?.overallProgress || 0) >= 100;

    if (isCompleted && !previousCompletedRef.current) {
      setCompletionDialogOpen(true);
    }

    previousCompletedRef.current = isCompleted;
  }, [playerState?.enrollment?.status, playerState?.overallProgress]);

  React.useEffect(() => {
    if (!completionDialogOpen || !playerState?.enrollment?.id) return;

    const fetchCompletionCertificate = async () => {
      try {
        const response = await certificateApi.get(`/api/certificates/enrollment/${playerState.enrollment.id}`);
        if (response.data?.status && response.data?.data) {
          setCompletionCertificate(response.data.data);
        } else {
          setCompletionCertificate(null);
        }
      } catch {
        setCompletionCertificate(null);
      }
    };

    void fetchCompletionCertificate();
  }, [completionDialogOpen, playerState?.enrollment?.id]);

  const handleDownloadCompletionCertificate = React.useCallback(async () => {
    try {
      const pdfUrl = completionCertificate?.pdfUrl;
      if (!pdfUrl) {
        showAlert("Certificate is not ready yet. You can download it from Certificates section later.", "warning", "Unavailable");
        return;
      }

      setDownloadingCertificate(true);
      const response = await certificateApi.get(pdfUrl, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(blob);

      const disposition = response.headers?.["content-disposition"] as string | undefined;
      const fileNameMatch = disposition?.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);
      const parsedFileName = fileNameMatch?.[1]
        ? decodeURIComponent(fileNameMatch[1].replace(/\"/g, ""))
        : null;

      const fallbackName = completionCertificate?.certificateNumber
        ? `${completionCertificate.certificateNumber}.pdf`
        : "course-certificate.pdf";

      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", parsedFileName || fallbackName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      showAlert("Certificate downloaded successfully", "success", "Download");
    } catch (error: any) {
      showAlert(error.message || "Failed to download certificate", "error", "Error");
    } finally {
      setDownloadingCertificate(false);
    }
  }, [completionCertificate?.certificateNumber, completionCertificate?.pdfUrl, showAlert]);

  React.useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const activeModule = React.useMemo(
    () => playerState?.modules.find((module) => module.id === activeModuleId) || null,
    [activeModuleId, playerState]
  );

  const activeContent = React.useMemo(
    () => activeModule?.content.find((content) => content.id === activeContentId) || activeModule?.content[0] || null,
    [activeContentId, activeModule]
  );

  const normalizedContentType = React.useMemo(
    () => String(activeContent?.type ?? "").trim().toLowerCase(),
    [activeContent?.type]
  );

  const pendingAssessment = React.useMemo(() => {
    if (!activeModule?.isCompleted) return null;
    const summary = activeModule.assessments.find((assessment) => Boolean(assessment.unlocksModuleID) && !assessment.passed);
    if (!summary) return null;
    return assessments.find((assessment) => assessment.id === summary.id) || null;
  }, [activeModule, assessments]);

  const activeAssessment = React.useMemo(() => {
    if (!activeAssessmentId) return pendingAssessment;
    return assessments.find((assessment) => assessment.id === activeAssessmentId) || pendingAssessment;
  }, [activeAssessmentId, assessments, pendingAssessment]);

  const activeAssessmentAttempt = activeAssessment?.id ? assessmentAttempts[activeAssessment.id] : null;
  const isAssessmentReadOnly = Boolean(activeAssessmentAttempt?.passed);
  const shouldShowAssessment = Boolean(isAssessmentActive && activeAssessment);

  const formatTime = (seconds: number) => {
    if (!seconds || Number.isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getMediaUrl = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    // Normalize Windows-style backslashes and ensure forward slashes
    const normalizedUrl = url.replace(/\\/g, "/");
    const baseUrl = `${COMPCHECK_URL}/api`;
    const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    return normalizedUrl.startsWith("/") ? `${cleanBase}${normalizedUrl}` : `${cleanBase}/${normalizedUrl}`;
  };

  const getVideoStreamUrl = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    // Use the streaming endpoint for proper range-request support
    const normalizedUrl = url.replace(/\\/g, "/");
    const relativePath = normalizedUrl.startsWith("/") ? normalizedUrl.slice(1) : normalizedUrl;
    return `${COMPCHECK_URL}/api/media/stream?path=${encodeURIComponent(relativePath)}`;
  };

  const resolveLocalizedMediaUrl = (media?: ModuleContent["imageURL"] | ModuleContent["audioURL"] | ModuleContent["videoURL"]) => {
    if (!media) return "";
    if (typeof media === "string") return media;
    if (!Array.isArray(media) || media.length === 0) return "";
    const preferredLanguage = (course?.courseLanguages?.[0] || "en").toLowerCase();
    const matched = media.find((entry) => (entry.lang || "en").toLowerCase() === preferredLanguage);
    return matched?.url || media[0]?.url || "";
  };

  const syncProgress = React.useCallback(async (watchedSeconds: number, durationSeconds?: number, completed?: boolean) => {
    if (!id || !activeContent?.id) return;

    setSyncing(true);
    try {
      setInlineError(null);
      const response = await courseApi.post(`/api/courses/${id}/content-progress`, {
        contentID: activeContent.id,
        watchedSeconds: Math.max(0, Math.floor(watchedSeconds)),
        durationSeconds: durationSeconds ? Math.max(0, Math.floor(durationSeconds)) : undefined,
        completed,
      });

      applyPlayerState(response.data?.data || response.data);
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to sync lesson progress";
      setInlineError(message);
      showAlert(message, "error", "Error");
    } finally {
      setSyncing(false);
    }
  }, [activeContent?.id, applyPlayerState, id, showAlert]);

  React.useEffect(() => {
    accumulatedWatchedRef.current = activeContent?.watchedSeconds || 0;
    lastPlaybackPositionRef.current = 0;
    lastSyncedSecondsRef.current = 0;
  }, [activeContent?.id, activeContent?.watchedSeconds]);

  const maybeSyncProgress = React.useCallback((watchedSeconds: number, durationSeconds?: number, completed?: boolean) => {
    const roundedSeconds = Math.floor(watchedSeconds);
    if (!completed && roundedSeconds - lastSyncedSecondsRef.current < 10) {
      return;
    }

    lastSyncedSecondsRef.current = roundedSeconds;
    void syncProgress(roundedSeconds, durationSeconds, completed);
  }, [syncProgress]);

  const handlePlaybackTimeUpdate = React.useCallback((currentTime: number, duration?: number) => {
    const previous = lastPlaybackPositionRef.current;
    if (previous > 0) {
      const delta = currentTime - previous;
      if (delta > 0 && delta <= 2.5) {
        accumulatedWatchedRef.current += delta;
      }
    }
    lastPlaybackPositionRef.current = currentTime;
    maybeSyncProgress(accumulatedWatchedRef.current, duration, false);
  }, [maybeSyncProgress]);

  const handleFullscreenToggle = async () => {
    if (!playerShellRef.current) return;
    if (!document.fullscreenElement) {
      await playerShellRef.current.requestFullscreen();
      return;
    }

    await document.exitFullscreen();
  };

  const handleModuleSelect = (moduleId?: string): boolean => {
    if (!moduleId || !playerState) return false;
    const module = playerState.modules.find((entry) => entry.id === moduleId);
    if (!module) return false;
    if (module.isLocked) {
      showAlert("This module is locked. Complete previous module and required assessment first.", "info", "Locked Module");
      return false;
    }

    setActiveModuleId(module.id || null);
    const nextContent = module.content.find((content) => !content.completed) || module.content[0] || null;
    setActiveContentId(nextContent?.id || null);
    setIsAssessmentActive(false);
    setActiveAssessmentId(null);
    return true;
  };

  const handleContentSelect = (contentId?: string) => {
    if (!contentId || !activeModule || activeModule.isLocked) return;
    setActiveContentId(contentId);
    setIsAssessmentActive(false);
    setActiveAssessmentId(null);
    lastSyncedSecondsRef.current = 0;
    setVideoLoading(true);
  };

  const hydrateAnswersFromAttempt = React.useCallback((attempt: AssessmentAttempt | null) => {
    if (!attempt?.answers?.length) return;
    const mapped = attempt.answers.reduce<Record<string, string[]>>((acc, answer) => {
      acc[answer.questionID] = answer.selectedOptions || [];
      return acc;
    }, {});
    setSelectedAnswers(mapped);
  }, []);

  const fetchLatestAttempt = React.useCallback(async (assessmentId: string) => {
    if (!playerState?.enrollment.id) return;
    try {
      const response = await assessmentApi.get(`/api/assessments/attempts/${assessmentId}/${playerState.enrollment.id}`);
      const attempts = response.data?.data || [];
      const latestAttempt = Array.isArray(attempts) && attempts.length > 0 ? attempts[0] : null;
      setAssessmentAttempts((prev) => ({ ...prev, [assessmentId]: latestAttempt }));
      hydrateAnswersFromAttempt(latestAttempt);
    } catch {
      // Keep player usable even if attempt history cannot be fetched.
    }
  }, [hydrateAnswersFromAttempt, playerState?.enrollment.id]);

  const advanceToNextContent = React.useCallback(() => {
    if (!playerState || !activeModule || !activeContent) return;

    const currentIndex = activeModule.content.findIndex((content) => content.id === activeContent.id);
    if (currentIndex >= 0 && currentIndex < activeModule.content.length - 1) {
      setActiveContentId(activeModule.content[currentIndex + 1].id || null);
      return;
    }

    if (pendingAssessment) return;

    const currentModuleIndex = playerState.modules.findIndex((module) => module.id === activeModule.id);
    const nextUnlockedModule = playerState.modules.slice(currentModuleIndex + 1).find((module) => !module.isLocked);
    if (nextUnlockedModule) {
      setActiveModuleId(nextUnlockedModule.id || null);
      setActiveContentId((nextUnlockedModule.content.find((content) => !content.completed) || nextUnlockedModule.content[0])?.id || null);
    }
  }, [activeContent, activeModule, pendingAssessment, playerState]);

  const handleAnswerToggle = (questionId: string, optionId: string) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: [optionId] }));
  };

  const handleSubmitAssessment = async () => {
    if (!activeAssessment || !playerState?.enrollment.id || !id) return;

    if (isAssessmentReadOnly) {
      showAlert("This assessment is in read-only mode because you already passed it.", "info", "Read Only");
      return;
    }

    const unanswered = activeAssessment.questions.filter((question) => (selectedAnswers[question.id] || []).length === 0);
    if (unanswered.length > 0) {
      showAlert("Please answer all questions before submitting.", "warning", "Incomplete Assessment");
      return;
    }

    setSubmittingAssessment(true);
    try {
      setInlineError(null);
      const submitResponse = await assessmentApi.post("/api/assessments/attempts", {
        assessmentID: activeAssessment.id,
        enrollmentID: playerState.enrollment.id,
        answers: activeAssessment.questions.map((question) => ({
          questionID: question.id,
          selectedOptions: selectedAnswers[question.id] || [],
        })),
      });

      const attempt = submitResponse.data?.data as AssessmentAttempt | undefined;
      const passed = Boolean(attempt?.passed);
      const percentage = Number(attempt?.percentage || 0);

      showAlert(
        passed
          ? `Assessment passed (${percentage.toFixed(2)}%). Next module unlocked.`
          : `Assessment submitted. You scored ${percentage.toFixed(2)}% and did not pass yet.`,
        passed ? "success" : "warning",
        passed ? "Passed" : "Not Passed"
      );

      if (attempt && activeAssessment.id) {
        setAssessmentAttempts((prev) => ({ ...prev, [activeAssessment.id]: attempt }));
      }

      setIsAssessmentActive(true);
      setActiveAssessmentId(activeAssessment.id);
      const response = await courseApi.get(`/api/courses/${id}/player-state`);
      applyPlayerState(response.data?.data || response.data);
      await fetchLatestAttempt(activeAssessment.id);
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to submit assessment";
      setInlineError(message);
      showAlert(message, "error", "Error");
    } finally {
      setSubmittingAssessment(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Loader loading={true} />
        <Footer />
      </DashboardLayout>
    );
  }

  if (!course || !playerState || !activeModule || !activeContent) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box py={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Course player unavailable</Typography>
              <Button variant="gradient" color="info" onClick={() => navigate(`/courses/${id}`)}>
                <ArrowBack sx={{ mr: 1 }} /> Back to Course
              </Button>
            </CardContent>
          </Card>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f4f7fb 0%, #eef3f8 100%)" }}>
        <Box sx={{ width: "100%", maxWidth: "100%", mx: "auto", px: { xs: 1, lg: 1.5 }, py: 2 }}>
          <Card sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 30px 80px rgba(15, 23, 42, 0.12)" }}>
            <Box sx={{ px: { xs: 2, lg: 3 }, py: 2.5, borderBottom: "1px solid #e8edf5", backgroundColor: "#fff" }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <IconButton onClick={() => navigate(`/courses/${id}`)}>
                    <ArrowBack />
                  </IconButton>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">{course.title}</Typography>
                    {course.instructorName && (
                      <Typography variant="button" color="text">Instructor: {course.instructorName}</Typography>
                    )}
                  </Box>
                </Box>
                <Box minWidth={{ xs: "100%", md: 320 }}>
                  <CourseProgressBar
                    label="Overall progress"
                    value={playerState.overallProgress}
                    height={8}
                    trackColor="#d9e2ef"
                    gradientEndColor="#378fe9"
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "360px 1fr" }, minHeight: "78vh" }}>
              <Box sx={{ borderRight: { lg: "1px solid #e8edf5" }, backgroundColor: "#fbfcfe" }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #e8edf5" }}>
                  <Typography variant="h6" fontWeight="bold">Course modules</Typography>
                  <Typography variant="button" color="text">Follow module order to unlock next sections.</Typography>
                </Box>

                <List sx={{ px: 1.5, py: 1.5 }}>
                  {playerState.modules.map((module, index) => {
                    const isActive = module.id === activeModule.id;
                    const moduleAssessmentSummary = module.assessments.find((assessment) => Boolean(assessment.unlocksModuleID));
                    const moduleAssessment = moduleAssessmentSummary
                      ? assessments.find((assessment) => assessment.id === moduleAssessmentSummary.id)
                      : null;
                    return (
                      <Box key={module.id} mb={1.25}>
                        <ListItemButton
                          selected={isActive}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleModuleSelect(module.id);
                          }}
                          disableRipple={module.isLocked}
                          aria-disabled={module.isLocked}
                          sx={{
                            borderRadius: 3,
                            alignItems: "flex-start",
                            border: isActive ? "1px solid #b6d7ff" : "1px solid transparent",
                            backgroundColor: isActive ? "#eef6ff" : "transparent",
                            opacity: module.isLocked ? 0.7 : 1,
                            cursor: module.isLocked ? "not-allowed" : "pointer",
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                            {module.isLocked ? (
                              <Lock color="disabled" fontSize="small" />
                            ) : module.isCompleted ? (
                              <CheckCircle sx={{ color: "#057642" }} fontSize="small" />
                            ) : (
                              <PlayArrow sx={{ color: "#0a66c2" }} fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={`${index + 1}. ${module.title}`}
                            secondary={
                              <Stack direction="row" spacing={0.75} mt={0.75} flexWrap="wrap">
                                <Chip size="small" label={module.isLocked ? "Locked" : module.isCompleted ? "Completed" : "In progress"} color={module.isLocked ? "default" : module.isCompleted ? "success" : "info"} />
                                {module.isLocked && <Chip size="small" icon={<Lock />} label="Locked module" color="default" variant="outlined" />}
                                {module.requiresAssessment && <Chip size="small" icon={<Quiz />} label={module.assessmentPassed ? "Assessment cleared" : "Assessment gate"} color={module.assessmentPassed ? "success" : "warning"} />}
                              </Stack>
                            }
                            primaryTypographyProps={{ fontWeight: 700, fontSize: 14, color: "#15202b" }}
                          />
                          <ChevronRight sx={{ color: "#7b8794", mt: 0.5 }} />
                        </ListItemButton>

                        {isActive && (
                          <Box sx={{ ml: 4.5, mt: 1, mr: 1 }}>
                            {module.content.map((content) => (
                              <ListItemButton
                                key={content.id}
                                onClick={() => handleContentSelect(content.id)}
                                sx={{ borderRadius: 2, mb: 0.5, py: 0.75, backgroundColor: content.id === activeContent.id ? "rgba(10, 102, 194, 0.08)" : "transparent" }}
                              >
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  {content.completed ? <CheckCircle sx={{ color: "#057642" }} fontSize="small" /> : <RadioButtonUnchecked sx={{ color: "#7b8794" }} fontSize="small" />}
                                </ListItemIcon>
                                <ListItemText
                                  primary={content.title}
                                  secondary={`${Math.round(content.progress || 0)}% watched`}
                                  primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                                  secondaryTypographyProps={{ fontSize: 11 }}
                                />
                              </ListItemButton>
                            ))}
                            {moduleAssessment && (
                              <ListItemButton
                                onClick={async () => {
                                  if (module.isLocked) {
                                    showAlert("This module is locked. Complete previous module and required assessment first.", "info", "Locked Module");
                                    return;
                                  }
                                  setSelectedAnswers({});
                                  setIsAssessmentActive(true);
                                  setActiveAssessmentId(moduleAssessment.id);
                                  await fetchLatestAttempt(moduleAssessment.id);
                                }}
                                sx={{ borderRadius: 2, mb: 0.5, py: 0.75, backgroundColor: shouldShowAssessment && activeAssessment?.id === moduleAssessment.id ? "rgba(10, 102, 194, 0.08)" : "transparent" }}
                              >
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <Quiz sx={{ color: moduleAssessmentSummary ? "#b26b00" : "#057642" }} fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={moduleAssessment.title}
                                  secondary={moduleAssessmentSummary?.passed ? "Passed (review only)" : "Required to unlock next module"}
                                  primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                                  secondaryTypographyProps={{ fontSize: 11 }}
                                />
                              </ListItemButton>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </List>
              </Box>

              <Box sx={{ backgroundColor: "#fff" }}>
                <Box sx={{ px: { xs: 2, lg: 3 }, py: 2.5 }}>
                  {inlineError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {inlineError}
                    </Alert>
                  )}
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2} flexWrap="wrap">
                    <Box>
                      <Typography variant="overline" color="info" fontWeight="bold">{activeModule.title}</Typography>
                      <Typography variant="h5" fontWeight="bold">{shouldShowAssessment ? activeAssessment?.title : activeContent.title}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {syncing && <Chip size="small" color="info" label="Syncing progress" />}
                      <Tooltip title={isFullscreen ? "Exit full screen" : "Full screen"}>
                        <IconButton onClick={handleFullscreenToggle}>
                          {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {!shouldShowAssessment && <Box ref={playerShellRef} sx={{ borderRadius: 3, overflow: "hidden", backgroundColor: "#0f172a", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)" }}>
                    {normalizedContentType === "image_audio" &&
                    (resolveLocalizedMediaUrl(activeContent.imageURL) || resolveLocalizedMediaUrl(activeContent.audioURL)) ? (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          minHeight: { xs: 280, sm: 360 },
                          maxHeight: "70vh",
                          display: "flex",
                          flexDirection: "column",
                          backgroundColor: "#0f172a",
                        }}
                      >
                        <Box
                          sx={{
                            flex: "1 1 auto",
                            minHeight: 0,
                            overflow: "auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#0f172a",
                            p: 1,
                          }}
                        >
                          <img
                            src={getMediaUrl(resolveLocalizedMediaUrl(activeContent.imageURL))}
                            alt={activeContent.title}
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          />
                        </Box>
                        <Box sx={{ flexShrink: 0, p: 2, backgroundColor: "rgba(15,23,42,0.92)" }}>
                          <audio
                            ref={audioRef}
                            src={getMediaUrl(resolveLocalizedMediaUrl(activeContent.audioURL))}
                            controls
                            style={{ width: "100%" }}
                            onPause={(event) => maybeSyncProgress(accumulatedWatchedRef.current, event.currentTarget.duration)}
                            onTimeUpdate={(event) => handlePlaybackTimeUpdate(event.currentTarget.currentTime, event.currentTarget.duration)}
                            onEnded={(event) => {
                              maybeSyncProgress(accumulatedWatchedRef.current, event.currentTarget.duration, false);
                              advanceToNextContent();
                            }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ position: "relative", width: "100%", pt: "56.25%" }}>
                        {normalizedContentType === "video" && resolveLocalizedMediaUrl(activeContent.videoURL) ? (
                          <>
                            {videoLoading && (
                              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(15,23,42,0.45)", zIndex: 1 }}>
                                <CircularProgress sx={{ color: "#fff" }} />
                              </Box>
                            )}
                            <video
                              ref={videoRef}
                              src={getVideoStreamUrl(resolveLocalizedMediaUrl(activeContent.videoURL))}
                              controls
                              playsInline
                              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
                              onLoadedData={() => setVideoLoading(false)}
                              onWaiting={() => setVideoLoading(true)}
                              onCanPlay={() => setVideoLoading(false)}
                              onPause={(event) => maybeSyncProgress(accumulatedWatchedRef.current, event.currentTarget.duration)}
                              onTimeUpdate={(event) => handlePlaybackTimeUpdate(event.currentTarget.currentTime, event.currentTarget.duration)}
                              onEnded={(event) => {
                                maybeSyncProgress(accumulatedWatchedRef.current, event.currentTarget.duration, false);
                                advanceToNextContent();
                              }}
                            />
                          </>
                        ) : (
                          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="h6" sx={{ color: "#fff" }}>This lesson has no playable media.</Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>}

                  {!shouldShowAssessment && <Card sx={{ mt: 2.5, borderRadius: 3, border: "1px solid #e8edf5", boxShadow: "none" }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={1.5}>
                        <Typography variant="h6" fontWeight="bold">Lesson status</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip icon={<Videocam />} size="small" label={normalizedContentType === "image_audio" ? "Image + audio" : "Video lesson"} />
                          <Chip icon={<Schedule />} size="small" label={`${formatTime(activeContent.watchedSeconds || 0)} watched`} color={activeContent.completed ? "success" : "info"} />
                          <Chip size="small" label={activeContent.completed ? "Completed" : `${Math.round(activeContent.progress || 0)}% complete`} color={activeContent.completed ? "success" : "default"} />
                        </Stack>
                      </Box>
                      <CourseProgressBar
                        label="Progress"
                        value={activeContent.progress || 0}
                        height={8}
                      />
                    </CardContent>
                  </Card>}

                  {shouldShowAssessment && activeAssessment && (
                    <Card sx={{ mt: 2.5, borderRadius: 3, border: "1px solid #f8d69d", background: "linear-gradient(180deg, #fffaf1 0%, #fff 100%)", boxShadow: "none" }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Box display="flex" alignItems="center" gap={1.25} mb={2}>
                          <Quiz sx={{ color: "#b26b00" }} />
                          <Box>
                            <Typography variant="h6" fontWeight="bold">Assessment unlock required</Typography>
                            <Typography variant="button" color="text">
                              Pass this assessment with at least {activeAssessment.passingScore}% to unlock the next gated module.
                              {isAssessmentReadOnly ? " You already passed it, so answers are read-only." : ""}
                            </Typography>
                          </Box>
                        </Box>

                        {activeAssessmentAttempt && (
                          <Alert severity={activeAssessmentAttempt.passed ? "success" : "warning"} sx={{ mb: 2 }}>
                            Last attempt: {activeAssessmentAttempt.percentage.toFixed(2)}% ({activeAssessmentAttempt.passed ? "Passed" : "Not passed"})
                          </Alert>
                        )}

                        <Typography variant="h6" fontWeight="bold" mb={2}>{activeAssessment.title}</Typography>
                        <Stack spacing={2}>
                          {activeAssessment.questions.map((question, questionIndex) => (
                            <Box key={question.id} sx={{ p: 2, borderRadius: 2.5, border: "1px solid #e8edf5", backgroundColor: "#fff" }}>
                              <Typography variant="button" fontWeight="bold" display="block" mb={1.25}>
                                {questionIndex + 1}. {question.question}
                              </Typography>
                              <Stack spacing={1}>
                                {question.options.map((option) => {
                                  const selected = (selectedAnswers[question.id] || []).includes(option.id);
                                  return (
                                    <Button
                                      key={option.id}
                                      variant={selected ? "gradient" : "outlined"}
                                      color={selected ? "info" : "secondary"}
                                      size="small"
                                      disabled={isAssessmentReadOnly}
                                      onClick={() => handleAnswerToggle(question.id, option.id)}
                                    >
                                      {option.text}
                                    </Button>
                                  );
                                })}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>

                        {!isAssessmentReadOnly && (
                          <Box mt={2.5} display="flex" justifyContent="flex-end">
                            <Button variant="gradient" color="info" onClick={handleSubmitAssessment} disabled={submittingAssessment} loading={submittingAssessment}>
                              Submit Assessment
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>

      <Dialog
        open={completionDialogOpen}
        onClose={() => setCompletionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Course Completed</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body1" fontWeight="bold">
              Congratulations! You have completed this course.
            </Typography>
            <Typography variant="body2" color="text">
              Your completion certificate is now available for download.
            </Typography>
            <Typography variant="body2" color="text">
              You can also go to the Certificates section anytime to download it later.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="text"
            color="secondary"
            onClick={() => setCompletionDialogOpen(false)}
          >
            Continue Viewing
          </Button>
          <Button
            variant="outlined"
            color="info"
            onClick={() => {
              setCompletionDialogOpen(false);
              navigate("/certificates");
            }}
          >
            Go to Certificates
          </Button>
          <Button
            variant="gradient"
            color="success"
            onClick={() => void handleDownloadCompletionCertificate()}
            loading={downloadingCertificate}
          >
            Download Certificate
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default CoursePlayer;